import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastVariants } from '@dotcom/crown-ui';
import { useAnalyticsContext } from '../../../context/analytics-context/analytics-context';
import { useAppContext } from '../../../context/app-context/app-context';
import { setIsToasterOpen } from '../../../context/app-context/app-context-actions';
import { useInitializationDataContext } from '../../../context/data-context/data-context';
import useAddressSelection from '../../../hooks/use-address-selection';
import { deleteAddress as deleteAddressService } from '../../../services/address';
import { useGetAddresses } from './use-get-addresses';

export const useDeleteAddress = () => {
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const { getAddresses } = useGetAddresses();
  const { isRecipientAddress, handleDeleteRecipientContact } = useAddressSelection();
  const { trackUpdateAddressFromAddressSummary } = useAnalyticsContext();

  const {
    initializedDataState: { data },
  } = useInitializationDataContext();
  const { appDispatch } = useAppContext();

  const projectId = data?.project_id;
  const accountId = data?.account_id;

  const deleteAddress: (contactId: string) => Promise<void> = useCallback(
    async (contactId: string) => {
      if (!projectId || !accountId) {
        throw new Error('Project ID and Account ID is required');
      }

      setIsLoading(true);
      let statusMessage = '';
      deleteAddressService(projectId, contactId)
        .then(() => {
          getAddresses(isRecipientAddress);
          handleDeleteRecipientContact(contactId);
        })
        .catch((error) => {
          setIsToasterOpen(appDispatch, {
            variant: ToastVariants.Error,
            title: t('recipientAddresses.delete.errorTitle'),
            children: t('recipientAddresses.delete.errorMessage') + error.message,
          });
          statusMessage = t('recipientAddresses.delete.errorMessage') + error.message;
        })
        .finally(() => {
          trackUpdateAddressFromAddressSummary('delete', statusMessage);
          setIsLoading(false);
        });
    },
    [projectId, accountId, getAddresses, isRecipientAddress, handleDeleteRecipientContact, appDispatch, t],
  );

  return { deleteAddress, isLoading };
};
